/**
 * Configurator — Settings sidebar that slides in from the right.
 *
 * Replicates OnMove-Admin Configurator:
 *  - Language selection (Select dropdown with nativeName)
 *  - Sidenav type (Dark / Transparent / White)
 *  - Navbar fixed toggle
 *  - Light / Dark mode toggle
 */

import { useState, useEffect } from "react";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import Icon from "@mui/material/Icon";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import ConfiguratorRoot from "components/Configurator/ConfiguratorRoot";

import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
  setDarkMode,
} from "context";
import { useI18n, useT } from "context/I18nContext";

function Configurator() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    openConfigurator,
    fixedNavbar,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [disabled, setDisabled] = useState(false);

  const { locale, setLocale, supportedLocales, localeInfo } = useI18n();
  const t = useT();

  useEffect(() => {
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }
    window.addEventListener("resize", handleDisabled);
    handleDisabled();
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
  };
  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
  };
  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  const handleDarkMode = () => {
    const goingLight = darkMode;
    setDarkMode(dispatch, !darkMode);
    // Sync sidenav style with theme
    setWhiteSidenav(dispatch, goingLight);
    setTransparentSidenav(dispatch, false);
  };

  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }) => ({
    height: pxToRem(39),
    background: darkMode
      ? white.main
      : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,
    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode
        ? white.main
        : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      {/* Header */}
      <KDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <KDBox>
          <KDTypography variant="h5">{t("settings.title")}</KDTypography>
        </KDBox>
        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </KDBox>

      <Divider />

      <KDBox pt={0.5} pb={3} px={3}>
        {/* ── Language ── */}
        <KDBox mb={3}>
          <KDTypography variant="h6" mb={1}>
            {t("settings.language")}
          </KDTypography>
          <KDTypography variant="caption" color="text" mb={2} display="block">
            {t("settings.languageDescription")}
          </KDTypography>
          <FormControl fullWidth size="small">
            <Select
              value={locale || "en"}
              onChange={(e) => setLocale(e.target.value)}
              renderValue={(selected) => {
                const info = localeInfo?.[selected];
                return info ? info.nativeName : selected;
              }}
              sx={{
                backgroundColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                color: darkMode ? "#fff" : "#344767",
                "& .MuiSelect-icon": {
                  color: darkMode ? "#fff" : "#344767",
                },
              }}
            >
              {supportedLocales.map((code) => {
                const info = localeInfo?.[code];
                return (
                  <MenuItem key={code} value={code}>
                    {info ? info.nativeName : code}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </KDBox>

        <Divider />

        {/* ── Sidenav Type ── */}
        <KDBox mt={3} lineHeight={1}>
          <KDTypography variant="h6">{t("settings.sidenavType")}</KDTypography>
          <KDBox sx={{ display: "flex", mt: 2, mr: 1 }}>
            <KDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={
                !transparentSidenav && !whiteSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {t("settings.dark")}
            </KDButton>
            <KDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <KDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                {t("settings.transparent")}
              </KDButton>
            </KDBox>
            <KDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled}
              fullWidth
              sx={
                whiteSidenav && !transparentSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {t("settings.white")}
            </KDButton>
          </KDBox>
        </KDBox>

        {/* ── Navbar Fixed ── */}
        <KDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          lineHeight={1}
        >
          <KDTypography variant="h6">{t("settings.navbarFixed")}</KDTypography>
          <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
        </KDBox>

        <Divider />

        {/* ── Light / Dark Mode ── */}
        <KDBox
          mt={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          lineHeight={1}
        >
          <KDTypography variant="h6">
            {darkMode ? t("settings.darkMode") : t("settings.lightMode")}
          </KDTypography>
          <Switch checked={darkMode} onChange={handleDarkMode} />
        </KDBox>

        <Divider />
      </KDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
